import { useState } from 'react';
import { useCredentialsValidation } from './useCredentialsValidation';
import { createActivity, createServiceAccount, updateServiceAccount, getServiceAccount } from '../api/activityApi';

/**
 * useActivityConfiguration - Shared hook for activity configuration flow
 * Used by both ActivityCreator (non-wizard) and WizardActivityConfigStep (wizard mode)
 *
 * Handles:
 * - Step management (service_account → configure → subscription → courses)
 * - Credentials validation
 * - Service account creation/update
 * - Activity creation
 */
export const useActivityConfiguration = ({
  childId,
  csrfToken,
  template,
  onComplete,
  onSkip,
  isWizardMode = false
}) => {
  // Step management
  const [currentStep, setCurrentStep] = useState('loading');
  const [credentials, setCredentials] = useState({});
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedGroupAlias, setSelectedGroupAlias] = useState(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [serviceAccountDetails, setServiceAccountDetails] = useState(null);
  const [useCustomAccount, setUseCustomAccount] = useState(false);
  const [usePreloadedAccount, setUsePreloadedAccount] = useState(false);
  const [isChangingAccount, setIsChangingAccount] = useState(false);

  // Credentials validation
  const {
    isValidating,
    validationError,
    validationSuccess,
    coursesData,
    courseStructure,
    courseGroups,
    validateCredentials,
    clearValidationState
  } = useCredentialsValidation(csrfToken);

  // Helper functions
  // Use server-provided is_external_service flag (centralized in ServiceQueryStrategies::Factory)
  const isExternalService = (tmpl) => {
    return tmpl?.is_external_service === true;
  };

  // Check if template uses OTP-based authentication (uses ArtWorkoutOtpForm instead of CredentialsForm)
  const usesOtpAuth = (tmpl) => {
    return tmpl?.activity_type === 'art_workout';
  };

  const getServiceName = (tmpl) => {
    if (!tmpl?.activity_type) {
      throw new Error(`Activity template ${tmpl?.key} is missing activity_type`);
    }
    return tmpl.activity_type;
  };

  // Subscription flags are NOT asked during activity creation
  // Users can set them by editing the activity later
  // This matches the main branch behavior
  const showsSubscriptionCheckbox = () => false;

  const getSubscriptionFlag = (tmpl) => {
    const activityType = tmpl?.activity_type;
    if (activityType === 'brilliant') return 'premium';
    return 'super';
  };

  const buildFlags = (tmpl, hasSub) => {
    if (!hasSub) return [];
    return [getSubscriptionFlag(tmpl)];
  };

  const getMaxCourses = () => template?.max_courses || 0;

  // Initialize the flow based on template type
  const initializeFlow = async () => {
    if (template) {
      if (template?.auto_course && isExternalService(template)) {
        // Auto-course external service (Duolingo, Brilliant, ArtWorkout, etc.)
        // Check if service account exists - if so, create activity immediately
        try {
          const result = await getServiceAccount(childId, getServiceName(template), csrfToken);
          if (result.exists) {
            // Service account exists - create activity immediately (no confirmation needed)
            // User can edit activity later to change account
            handleCreateActivityWithExistingAccount(result.active_details);
            return;
          }
        } catch (err) {
          console.warn('Error checking service account:', err);
        }
        // No existing account - proceed with normal flow
        if (usesOtpAuth(template)) {
          setCurrentStep('configure');
        } else {
          setCurrentStep('service_account');
        }
      } else if (usesOtpAuth(template)) {
        // OTP-based auth without auto_course - go to configure step
        setCurrentStep('configure');
      } else if (isExternalService(template)) {
        setCurrentStep('service_account');
      } else {
        // Preloaded activity - create immediately
        handleCreatePreloadedActivity();
      }
    }
  };

  // Create preloaded activity directly (non-external services)
  const handleCreatePreloadedActivity = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const activityData = {
        activity_type: template.activity_type,
        status: 'active'
      };
      if (template.activity_subtype) {
        activityData.activity_sub_type = template.activity_subtype;
      }

      const result = await createActivity(childId, activityData, csrfToken);
      if (onComplete) {
        onComplete(isWizardMode ? template.key : result);
      }
    } catch (error) {
      setSubmitError(error.message);
      setIsSubmitting(false);
      setCurrentStep('error');
    }
  };

  // Handle service account ready
  const handleServiceAccountReady = (details, isCustom = false, isPreloaded = false, isChanging = false, existingFlags = null) => {
    setIsChangingAccount(isChanging);

    if (existingFlags?.includes('super') || existingFlags?.includes('premium')) {
      setHasSubscription(true);
    }

    if (isCustom) {
      setUseCustomAccount(true);
      setUsePreloadedAccount(false);
      setServiceAccountDetails(null);
      clearValidationState();
      if (details) {
        setCredentials({
          ...(details.username && { username: details.username }),
          ...(details.email && { email: details.email }),
          ...(details.password && { password: details.password })
        });
      }
      setCurrentStep('configure');
    } else if (details) {
      setUseCustomAccount(false);
      setUsePreloadedAccount(isPreloaded);
      setServiceAccountDetails(details);

      // For OTP-based auth (like ArtWorkout) with existing account, skip validation
      // and create activity directly since we already have valid tokens
      if (usesOtpAuth(template) && !isChanging && template?.auto_course) {
        handleCreateActivityWithExistingAccount(details);
      } else {
        handleValidateWithServiceAccount(details, isPreloaded);
      }
    }
  };

  // Create activity directly using existing service account (for OTP-based auth)
  // Skip validation since we already have valid tokens in the service account
  const handleCreateActivityWithExistingAccount = async (details) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Build activity data with auto_course
      const activityData = {
        activity_type: template.activity_type,
        status: 'active',
        external_config: {
          courses: [template.auto_course || 'default']
        }
      };
      // Include activity_sub_type if template has it (e.g., duolingo-music has subtype "music")
      if (template.activity_subtype) {
        activityData.activity_sub_type = template.activity_subtype;
      }

      const result = await createActivity(childId, activityData, csrfToken);

      setIsSubmitting(false);
      if (onComplete) {
        onComplete(isWizardMode ? template.key : result);
      }
    } catch (error) {
      setSubmitError(error.message);
      setIsSubmitting(false);
    }
  };

  // Validate credentials using service account details
  const handleValidateWithServiceAccount = async (details, isPreloaded = false) => {
    // Build credentials based on what's available - Brilliant uses email, others use username
    const creds = {
      ...(details.username && { username: details.username }),
      ...(details.email && { email: details.email }),
      ...(details.password && { password: details.password })
    };
    setCredentials(creds);
    const result = await validateCredentials(creds, getServiceName(template));

    if (result.success) {
      if (template?.auto_course) {
        setSelectedCourses([template.auto_course]);
        if (showsSubscriptionCheckbox()) {
          setCurrentStep('subscription');
        } else {
          await handleSubmitActivityWithCourses(creds, [template.auto_course], isPreloaded);
        }
      } else if (showsSubscriptionCheckbox()) {
        setCurrentStep('subscription');
      } else {
        setCurrentStep('courses');
      }
    } else if (isPreloaded || usePreloadedAccount) {
      setCurrentStep('preloaded_error');
    } else {
      setUseCustomAccount(true);
      setServiceAccountDetails(null);
      setCurrentStep('configure');
    }
  };

  // Handle credentials validation (for custom accounts)
  const handleValidateCredentials = async (creds) => {
    setCredentials(creds);
    const result = await validateCredentials(creds, getServiceName(template));

    if (result.success) {
      if (template?.auto_course) {
        setSelectedCourses([template.auto_course]);
      }

      if (showsSubscriptionCheckbox()) {
        setCurrentStep('subscription');
      } else if (template?.auto_course) {
        if (useCustomAccount && !isChangingAccount) {
          try {
            await createServiceAccount(childId, getServiceName(template), false, creds, csrfToken);
          } catch (err) {
            console.warn('Service account creation warning:', err.message);
          }
        }
        await handleSubmitActivityWithCourses(creds, [template.auto_course]);
      } else {
        if (useCustomAccount && !isChangingAccount) {
          try {
            await createServiceAccount(childId, getServiceName(template), false, creds, csrfToken);
          } catch (err) {
            console.warn('Service account creation warning:', err.message);
          }
        }
        setCurrentStep('courses');
      }
    }
  };

  // Handle subscription continue
  const handleSubscriptionContinue = async () => {
    const flags = buildFlags(template, hasSubscription);
    if (useCustomAccount && !isChangingAccount) {
      try {
        await createServiceAccount(childId, getServiceName(template), false, credentials, csrfToken, flags);
      } catch (err) {
        console.warn('Service account creation warning:', err.message);
      }
    }

    if (template?.auto_course) {
      await handleSubmitActivityWithCourses(credentials, selectedCourses);
    } else {
      setCurrentStep('courses');
    }
  };

  // Handle course selection
  const handleCourseToggle = (courses) => setSelectedCourses(courses);

  const handleGroupSelect = (groupAlias) => {
    setSelectedGroupAlias(groupAlias);
    const group = courseGroups?.find(g => g.alias === groupAlias);
    if (group) {
      setSelectedCourses(group.courses || []);
    }
  };

  // Submit activity with courses
  const handleSubmitActivityWithCourses = async (creds, courses, isPreloaded = usePreloadedAccount) => {
    setIsSubmitting(true);
    setSubmitError(null);
    const flags = buildFlags(template, hasSubscription);

    try {
      // Handle service account creation/update
      if (isExternalService(template)) {
        if (isChangingAccount) {
          if (isPreloaded) {
            await updateServiceAccount(childId, getServiceName(template), true, null, csrfToken, flags);
          } else if (useCustomAccount) {
            await updateServiceAccount(childId, getServiceName(template), false, creds, csrfToken, flags);
          }
        } else if (isPreloaded) {
          await createServiceAccount(childId, getServiceName(template), true, null, csrfToken, flags);
        } else if (showsSubscriptionCheckbox() && !useCustomAccount && !isPreloaded) {
          await updateServiceAccount(childId, getServiceName(template), false, creds, csrfToken, flags);
        }
      }

      // Build activity data
      const activityData = {
        activity_type: template.activity_type,
        status: 'active'
      };
      if (template.activity_subtype) {
        activityData.activity_sub_type = template.activity_subtype;
      }
      if (isExternalService(template)) {
        activityData.external_config = {
          ...creds,
          courses: courses
        };
        if (template.activity_type === 'codecombat' && selectedGroupAlias) {
          activityData.external_config.group_alias = selectedGroupAlias;
        }
      }

      const result = await createActivity(childId, activityData, csrfToken);

      setIsSubmitting(false);
      if (onComplete) {
        onComplete(isWizardMode ? template.key : result);
      }
    } catch (error) {
      setSubmitError(error.message);
      setIsSubmitting(false);
    }
  };

  // Handle course submission
  const handleSubmitCourses = async () => {
    if (selectedCourses.length === 0) {
      setSubmitError('Please select at least one course');
      return;
    }

    const maxCourses = getMaxCourses();
    if (maxCourses > 0 && selectedCourses.length > maxCourses) {
      setSubmitError(`Bạn chỉ có thể chọn tối đa ${maxCourses} khóa học`);
      return;
    }

    await handleSubmitActivityWithCourses(credentials, selectedCourses);
  };

  // Handle ArtWorkout OTP success - create service account with credentials, then create activity
  const handleArtWorkoutOtpSuccess = async ({ credentials }) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Create service account with credentials from OTP verification
      const flags = buildFlags(template, hasSubscription);
      await createServiceAccount(childId, getServiceName(template), false, credentials, csrfToken, flags);

      // Build activity data
      const activityData = {
        activity_type: template.activity_type,
        status: 'active',
        external_config: {
          courses: [template.auto_course || 'default']
        }
      };
      // Include activity_sub_type if template has it
      if (template.activity_subtype) {
        activityData.activity_sub_type = template.activity_subtype;
      }

      const result = await createActivity(childId, activityData, csrfToken);

      setIsSubmitting(false);
      if (onComplete) {
        onComplete(isWizardMode ? template.key : result);
      }
    } catch (error) {
      setSubmitError(error.message);
      setIsSubmitting(false);
    }
  };

  // Handle back navigation
  const handleBack = (onBackToTemplates) => {
    if (currentStep === 'courses') {
      if (showsSubscriptionCheckbox()) {
        setCurrentStep('subscription');
      } else if (useCustomAccount) {
        setCurrentStep('configure');
      } else {
        setCurrentStep('service_account');
        setUsePreloadedAccount(false);
        setIsChangingAccount(false);
        setCredentials({});
      }
      setSelectedCourses([]);
      setSelectedGroupAlias(null);
    } else if (currentStep === 'subscription') {
      if (useCustomAccount) {
        setCurrentStep('configure');
      } else {
        setCurrentStep('service_account');
        setUsePreloadedAccount(false);
        setIsChangingAccount(false);
        setCredentials({});
      }
      setHasSubscription(false);
    } else if (currentStep === 'configure') {
      if (usesOtpAuth(template)) {
        // OTP-based auth - go back to template selection
        if (onBackToTemplates) {
          onBackToTemplates();
        }
      } else {
        setCurrentStep('service_account');
      }
      setCredentials({});
      setUseCustomAccount(false);
      setIsChangingAccount(false);
    } else if (currentStep === 'preloaded_error') {
      setCurrentStep('service_account');
      setUsePreloadedAccount(false);
      setIsChangingAccount(false);
      setCredentials({});
    } else if (currentStep === 'service_account') {
      if (onBackToTemplates) {
        onBackToTemplates();
      }
    }
  };

  // Handle skip (wizard mode only)
  const handleSkip = () => {
    if (onSkip) {
      onSkip(template?.key);
    }
  };

  // Reset all state
  const resetState = () => {
    setCurrentStep('loading');
    setCredentials({});
    setSelectedCourses([]);
    setSelectedGroupAlias(null);
    setHasSubscription(false);
    setIsSubmitting(false);
    setSubmitError(null);
    setServiceAccountDetails(null);
    setUseCustomAccount(false);
    setUsePreloadedAccount(false);
    setIsChangingAccount(false);
    clearValidationState();
  };

  return {
    // State
    currentStep,
    credentials,
    selectedCourses,
    selectedGroupAlias,
    hasSubscription,
    isSubmitting,
    submitError,
    serviceAccountDetails,
    useCustomAccount,
    usePreloadedAccount,
    isChangingAccount,

    // Validation state
    isValidating,
    validationError,
    validationSuccess,
    coursesData,
    courseStructure,
    courseGroups,

    // Computed
    isExternalService: isExternalService(template),
    usesOtpAuth: usesOtpAuth(template),
    showsSubscriptionCheckbox: showsSubscriptionCheckbox(),
    getServiceName: () => getServiceName(template),
    getMaxCourses,

    // Actions
    initializeFlow,
    handleServiceAccountReady,
    handleValidateCredentials,
    handleSubscriptionContinue,
    handleCourseToggle,
    handleGroupSelect,
    handleSubmitCourses,
    handleArtWorkoutOtpSuccess,
    handleBack,
    handleSkip,
    handleCreatePreloadedActivity,
    setHasSubscription,
    resetState
  };
};
